<template>
    <div class="ah-data-table-wrapper">
        <div class="top-actions">
            <div class="top-left-actions">
                <slot name="top-left-actions"></slot>
            </div>
            <div class="top-right-actions">
                <slot name="top-right-actions"></slot>
                <el-tooltip v-if="showRefreshButton" class="item" effect="light" content="重新整理" placement="top">
                    <el-button type="normal" size="small" :loading="isLoading" @click="$emit('refresh')" icon="el-icon-refresh"></el-button>
                </el-tooltip>
                <el-popover
                    v-if="columnFilter"
                    class="ah-data-table-action-popover"
                    placement="bottom-end"
                    min-width="170"
                    trigger="click"
                    :value="filterPanelVisible"
                    transition=""
                    :manual="true">
                    <div class="ah-data-table-action-panel">
                        <div class="panel-title">
                            <div>顯示欄位</div>
                            <el-button size="mini" @click="onResetColVisible">重設</el-button>
                        </div>
                        <div class="panel-body">
                            <div class="column-visible-check-list">
                                <div
                                    v-for="(colData, colKey) in localColumns"
                                    :key="colKey"
                                    class="column-visible-check-item"
                                >
                                    <template v-if="colKey !== 'selection'">
                                        <el-checkbox
                                            v-model="colData.visible"
                                            :disabled="colData.disableHide"
                                            @change="onCheckFilterColumn($event, colKey)"
                                        >
                                            <div>{{ colData.label }}</div>
                                        </el-checkbox>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <span slot="reference">
                        <el-tooltip class="item" effect="light" content="自訂欄位顯示" placement="top">
                            <el-button
                                size="small"
                                icon="el-icon-s-operation"
                                @click="onClickColumnFilterButton"
                            ></el-button>
                        </el-tooltip>
                    </span>
                </el-popover>
            </div>
        </div>
        <el-table
            :data="data"
            class="ah-el-data-table"
            :class="{ 'row-click': isRowClick }"
            v-loading="isLoading"
            :empty-text="isLoading ? '' : emptyText"
            style="width: 100%"
            :header-cell-class-name="getHeadCellClassName"
            :cell-class-name="getBodyCellClassName"
            @row-click="onRowClick"
            @selection-change="onSelectionChange"
        >
            <el-table-column
                v-if="localColumns.selection"
                type="selection"
                width="45">
            </el-table-column>
            <el-table-column
                v-if="localColumns.index && localColumns.index.visible"
                type="index"
                label="#"
                class-name="index-cell"
                :index="indexMethod"
                width="46"
            >
            </el-table-column>
            <template v-for="(colData, colKey) in localColumns">
                <el-table-column
                    v-if="colData.visible && !['index', 'selection'].includes(colKey)"
                    :key="colKey"
                    :prop="colKey"
                    :label="colData.label"
                    :width="colData.width"
                    :min-width="colData.minWidth"
                    :align="colData.align ? colData.align : null"
                    :show-overflow-tooltip="colData.showOverflowTooltip ? colData.align : false"
                >
                    <template slot="header" v-if="colData.tableHeader">
                        <div v-html="colData.tableHeader"></div>
                    </template>
                    <template slot-scope="scope">
                        <slot
                            :name="colKey"
                            v-bind:row="scope.row"
                            v-bind:column="colData"
                            v-bind:columns="localColumns"
                        >{{ scope.row[colKey] }}</slot>
                    </template>
                </el-table-column>
            </template>
        </el-table>
        <div class="bottom-actions">
            <div class="bottom-left-actions">
                <slot name="bottom-left-actions"></slot>
            </div>
            <div class="bottom-right-actions">
                <slot name="bottom-right-actions"></slot>
            </div>
        </div>
    </div>
</template>
<script>
export default {
    name: 'ahDataTable',
    components: { },
    props: {
        tableKey: {
            type: String,
            default: null,
        },
        columnFilter: {
            type: Boolean,
            default: true,
        },
        columns: {
            type: Object,
            default: null,
        },
        data: {
            type: Array,
            default: null,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isRowClick: {
            type: Boolean,
            default: true,
        },
        emptyText: {
            type: String,
            default: '沒有資料',
        },
        indexStart: {
            type: Number,
            default: 1,
        },
        showRefreshButton: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            localColumns: {},
            filterPanelVisible: false,
            localIndexVisible: this.indexVisible,
        };
    },
    beforeMount() {
        if (this.columns) {
            this.initLocalColumnsFromPropsColumns();
        }
    },
    watch: {
        columns() {
            this.initLocalColumnsFromPropsColumns();
        },
    },
    methods: {
        initLocalColumnsFromPropsColumns() {
            // 把 props.columns 複製一份變為 localColumns
            const localColumns = JSON.parse(JSON.stringify(this.columns));

            // 在每個 columns 添加屬性: visible
            // visible 依據 localStorage 是否有隱藏紀錄
            Object.keys(localColumns).forEach((colKey) => {
                const lsData = localStorage.getItem(`${this.tableKey}-${colKey}-visible`);
                if (lsData === 'true') {
                    localColumns[colKey].visible = true;
                } else if (lsData === 'false') {
                    localColumns[colKey].visible = false;
                } else {
                    localColumns[colKey].visible = !localColumns[colKey].hideByDefault;
                }
            });
            this.localColumns = localColumns;
        },
        indexMethod(index) {
            return index + this.indexStart;
        },
        getHeadCellClassName(cell) {
            return `head-cell-${cell.column.property}`;
        },
        getBodyCellClassName(cell) {
            return `body-cell-${cell.column.property}`;
        },
        onRowClick(row, column, event) {
            this.$emit('row-click', row, column, event);
        },
        onSelectionChange(selectedItems) {
            this.$emit('selection-change', selectedItems);
        },
        onClickColumnFilterButton() {
            setTimeout(() => {
                this.filterPanelVisible = true;
            }, 1);
        },
        onCheckFilterColumn(checked, colKey) {
            if (this.tableKey) {
                const lsKey = `${this.tableKey}-${colKey}-visible`;
                if (checked) {
                    localStorage.setItem(lsKey, 'true');
                } else {
                    localStorage.setItem(lsKey, 'false');
                }
            }
            this.$emit('filterChanged', colKey, checked);
        },
        onResetColVisible() {
            Object.keys(this.localColumns).forEach((colKey) => {
                localStorage.removeItem(`${this.tableKey}-${colKey}-visible`);
                this.localColumns[colKey].visible = !this.localColumns[colKey].hideByDefault;
            });
            this.filterPanelVisible = false;
        },
    },
};

</script>
<style lang="scss">
.ah-data-table-wrapper {
    >.top-actions,
    >.bottom-actions {
        display: flex;
        justify-content: space-between;

        >.top-left-actions,
        >.top-right-actions,
        >.bottom-left-actions,
        >.bottom-right-actions {
            display: flex;
            align-items: flex-start;
            gap: 8px;

            .el-button + .el-button {
                margin-left: 0;
            }
        }
    }
    >.bottom-actions {
        margin-top: 10px;
    }
    td.index-cell > .cell {
        font-size: 9.5pt;
        color: #888;
    }
}
.ah-data-table-action-panel {
    .panel-title {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: #666;
        font-size: 9pt;
        border-bottom: 1px solid #eee;
        padding: 0 0 5px 0;
        margin-bottom: 10px;

        .el-button--mini {
            padding: 4px 8px;
            font-size: 10px;
        }
    }
    .panel-body {
        padding: 0 5px;
    }
    .column-visible-check-list {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .el-checkbox {
            // font-weight: normal;
            font-size: 10pt;
            color: #999;

            &.is-checked.is-disabled {
                span.el-checkbox__label {
                    color: #463cb2 !important;
                }
            }
        }
    }
}
</style>
